import { Action, Selector, State, StateContext } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';

import { DocumentManagementService } from './document-management.service';
import { IEntityDocument, UUID } from '../../core/interfaces';
import { defaultDocumentsState, DocumentsModel } from './document-management.model';
import { Documents } from "./document-management.actions";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Entity } from "../secure-pro-state/secure-pro.actions";
import { SecureFile } from "../secure-file-state/secure-file.actions";

@State<DocumentsModel>({
  name: 'documents',
  defaults: { ...defaultDocumentsState },
})
@Injectable()
export class DocumentManagementState {
  private documentService: DocumentManagementService = inject(DocumentManagementService);
  private snackbar: MatSnackBar = inject(MatSnackBar);

  @Selector()
  static getIsLoading(state: DocumentsModel) {
    return state.isDocumentsLoading;
  }

  @Selector()
  static getDocuments(state: DocumentsModel) {
    return state.documentsMap;
  }

  @Selector()
  static getIsCustomDocumentTypesLoading(state: DocumentsModel) {
    return state.isCustomDocumentTypesLoading;
  }

  @Selector()
  static customDocumentTypesMap(state: DocumentsModel) {
    return state.customDocumentTypesMap;
  }

  @Action(Documents.GetByIds)
  async getByIds(
    { patchState, dispatch }: StateContext<DocumentsModel>,
    { documentIds }: Documents.GetByIds
  ): Promise<void> {
    try {
      patchState({ isDocumentsLoading: true });

      const documents = await this.documentService.getDocuments(documentIds);

      dispatch(new Documents.GetByIdsSuccess(documents));
    } catch (error) {
      dispatch(new Documents.GetByIdsFail(error));
    }
  }

  @Action(Documents.GetByIdsSuccess)
  async getByIdsSuccess(
    { patchState, getState }: StateContext<DocumentsModel>,
    { documents }: Documents.GetByIdsSuccess,
  ): Promise<void> {
    const documentsMap = documents.reduce((acc, doc) => {
      acc[doc.id] = doc;
      return acc;
    }, {} as { [id in UUID]: IEntityDocument });

    patchState({
      documentsMap: {
        ...getState().documentsMap,
        ...documentsMap
      },
      isDocumentsLoading: false,
    });
  }

  @Action(Documents.GetByIdsFail)
  async getByIdsFail(
    { patchState }: StateContext<DocumentsModel>,
    { error }: Documents.GetByIdsFail,
  ): Promise<void> {
    patchState({ isDocumentsLoading: false });

    this.snackbar.open(
      "Something went wrong while fetching documents",
      "Dismiss", { duration: 5000 }
    )
    console.error(error)
  }

  @Action(Documents.Create)
  async create(
    { patchState, dispatch }: StateContext<DocumentsModel>,
    { documents, files, orgId, entityId, documentTypeId }: Documents.Create
  ): Promise<void> {
    try {
      patchState({ isDocumentsLoading: true });

      const createdDocumentPromises = documents.map(async (document, i) => {
        const file = files[i];
        const docVersion = await this.documentService.uploadFileVersion(document.id, file);

        return this.documentService.createDocument({
          ...document,
          versions: [
            docVersion
          ]
        });
      });

      const createdDocuments = await Promise.all(createdDocumentPromises);
      if (orgId) {
        dispatch(new Entity.AddDocumentsToType(orgId, entityId, documentTypeId, createdDocuments));
      } else {
        dispatch(new SecureFile.AddDocumentsToType(entityId, documentTypeId, createdDocuments));
      }

      dispatch(new Documents.CreateSuccess(createdDocuments));
    } catch (error) {
      dispatch(new Documents.CreateFail(error));
    }
  }

  @Action(Documents.CreateSuccess)
  async createSuccess(
    { patchState, getState }: StateContext<DocumentsModel>,
    { documents }: Documents.CreateSuccess
  ): Promise<void> {
    const documentsMap = { ...getState().documentsMap }

    for (const document of documents) {
      documentsMap[document.id] = document;
    }

    patchState({
      documentsMap,
      isDocumentsLoading: false,
    })

    this.snackbar.open(
      "Documents were created successfully",
      "Dismiss", { duration: 5000 }
    )
  }

  @Action(Documents.CreateFail)
  async createFail(
    { patchState }: StateContext<DocumentsModel>,
    { error }: Documents.CreateFail,
  ): Promise<void> {
    patchState({ isDocumentsLoading: false });

    this.snackbar.open(
      "Something went wrong while creating document",
      "Dismiss", { duration: 5000 }
    )
    console.error(error)
  }

  @Action(Documents.Update)
  async update(
    { patchState, dispatch }: StateContext<DocumentsModel>,
    { document }: Documents.Update
  ): Promise<void> {
    try {
      patchState({ isDocumentsLoading: true });

      const updatedDocument = await this.documentService.updateDocument(document);

      dispatch(new Documents.UpdateSuccess(updatedDocument));
    } catch (error) {
      dispatch(new Documents.UpdateFail(error));
    }
  }

  @Action(Documents.UpdateSuccess)
  async updateSuccess(
    { getState, patchState }: StateContext<DocumentsModel>,
    { document }: Documents.UpdateSuccess
  ): Promise<void> {
    patchState({
      documentsMap: {
        ...getState().documentsMap,
        [document.id]: document,
      },
    });

    this.snackbar.open(
      "Document successfully updated",
      "Dismiss", { duration: 5000 }
    )
  }

  @Action(Documents.UpdateFail)
  async updateFail(
    { patchState }: StateContext<DocumentsModel>,
    { error }: Documents.UpdateFail,
  ): Promise<void> {
    patchState({ isDocumentsLoading: false });

    this.snackbar.open(
      "Something went wrong while updating document",
      "Dismiss", { duration: 5000 }
    )
    console.error(error)
  }


  @Action(Documents.UploadVersion)
  async uploadVersion(
    { getState, patchState, dispatch }: StateContext<DocumentsModel>,
    { documentId, file }: Documents.UploadVersion
  ): Promise<void> {
    try {
      patchState({ isDocumentsLoading: true });

      const document = {
        ...getState().documentsMap[documentId]
      };

      if (!document || !document.id) {
        throw new Error("Document to update not found");
      }

      const newVersion = await this.documentService.uploadFileVersion(documentId, file);

      document.versions = [
        ...(document.versions || []),
        newVersion,
      ];

      const updatedDocument = await this.documentService.updateDocument(document as IEntityDocument);

      dispatch(new Documents.ManageVersionSuccess(updatedDocument));
    } catch (error) {
      dispatch(new Documents.ManageVersionFail(error));
    }
  }

  @Action(Documents.ManageVersionSuccess)
  async manageVersionsSuccess(
    { getState, patchState }: StateContext<DocumentsModel>,
    { document }: Documents.ManageVersionSuccess
  ): Promise<void> {
    patchState({
      documentsMap: {
        ...getState().documentsMap,
        [document.id]: document,
      },
    });

    this.snackbar.open(
      "Document versions successfully updated",
      "Dismiss", { duration: 5000 }
    )
  }

  @Action(Documents.ManageVersionFail)
  async manageVersionsFail(
    { patchState }: StateContext<DocumentsModel>,
    { error }: Documents.ManageVersionFail,
  ): Promise<void> {
    patchState({ isDocumentsLoading: false });

    this.snackbar.open(
      "Something went wrong while updating document versions",
      "Dismiss", { duration: 5000 }
    )
    console.error(error)
  }

  @Action(Documents.DeleteVersion)
  async deleteVersion(
    { getState, patchState, dispatch }: StateContext<DocumentsModel>,
    { documentId, versionIndex }: Documents.DeleteVersion
  ): Promise<void> {
    try {
      patchState({ isDocumentsLoading: true });

      const document = {
        ...getState().documentsMap[documentId]
      };

      if (!document || !document.id) {
        throw new Error("Document to update not found");
      }

      const versionToDelete = document.versions?.[versionIndex];

      if (!versionToDelete) {
        throw new Error("Version to delete not found");
      }

      await this.documentService.deleteFileVersion(documentId, versionIndex);
      document.versions = (document.versions || []).filter(v => v.fileRef !== versionToDelete.fileRef);

      const updatedDocument = await this.documentService.updateDocument(document as IEntityDocument)

      dispatch(new Documents.ManageVersionSuccess(updatedDocument));
    } catch (error) {
      dispatch(new Documents.ManageVersionFail(error));
    }
  }

  @Action(Documents.Delete)
  async delete(
    { patchState, dispatch }: StateContext<DocumentsModel>,
    { document }: Documents.Delete
  ): Promise<void> {
    try {
      patchState({ isDocumentsLoading: true });

      const deletedDocId = await this.documentService.deleteDocument(document.id);

      dispatch(new Documents.DeleteSuccess(deletedDocId));
    } catch (error) {
      dispatch(new Documents.DeleteFail(error));
    }
  }

  @Action(Documents.DeleteSuccess)
  async deleteSuccess(
    { getState, patchState }: StateContext<DocumentsModel>,
    { documentId }: Documents.DeleteSuccess
  ): Promise<void> {
    const documentsMap = {
      ...getState().documentsMap,
    };

    delete documentsMap[documentId];

    patchState({
      documentsMap,
    });

    this.snackbar.open(
      "Document successfully deleted",
      "Dismiss", { duration: 5000 }
    )
  }

  @Action(Documents.DeleteFail)
  async deleteFail(
    { patchState }: StateContext<DocumentsModel>,
    { error }: Documents.DeleteFail,
  ): Promise<void> {
    patchState({ isDocumentsLoading: false });

    this.snackbar.open(
      "Something went wrong while deleting document",
      "Dismiss", { duration: 5000 }
    )
    console.error(error)
  }

  @Action(Documents.GetCustomDocumentTypes)
  async getCustomDocTypes(
    { dispatch, patchState }: StateContext<DocumentsModel>,
    { orgId }: Documents.GetCustomDocumentTypes
  ): Promise<void> {
    try {
      patchState({ isCustomDocumentTypesLoading: true });

      const customDocTypes = await this.documentService.getCustomDocumentTypes(orgId);

      dispatch(new Documents.GetCustomDocumentTypesSuccess(orgId, customDocTypes));
    } catch (error) {
      dispatch(new Documents.GetCustomDocumentTypesFail(error));
    }
  }

  @Action(Documents.GetCustomDocumentTypesSuccess)
  getCustomDocTypesSuccess(
    { patchState, getState }: StateContext<DocumentsModel>,
    { orgId, customDocType }: Documents.GetCustomDocumentTypesSuccess
  ): void {
    const state = getState();

    patchState({
      isCustomDocumentTypesLoading: false,
      customDocumentTypesMap: {
        ...state.customDocumentTypesMap,
        [orgId]: customDocType
      }
    });
  }

  @Action(Documents.GetCustomDocumentTypesFail)
  getCustomDocTypesFail(
    { patchState }: StateContext<DocumentsModel>,
    { error }: Documents.GetCustomDocumentTypesFail
  ): void {
    patchState({ isCustomDocumentTypesLoading: false });

    this.snackbar.open("Failed to fetch custom document types", "Dismiss", { duration: 5000 });
    console.error(error);
  }

  @Action(Documents.SetCustomDocumentTypes)
  async setCustomDocTypes(
    { dispatch, patchState }: StateContext<DocumentsModel>,
    { orgId, customDocType }: Documents.SetCustomDocumentTypes
  ): Promise<void> {
    try {
      patchState({ isCustomDocumentTypesLoading: true });

      await this.documentService.setCustomDocumentTypes(orgId, customDocType);

      dispatch(new Documents.SetCustomDocumentTypesSuccess(orgId, customDocType));
    } catch (error) {
      dispatch(new Documents.SetCustomDocumentTypesFail(error));
    }
  }

  @Action(Documents.SetCustomDocumentTypesSuccess)
  setCustomDocTypesSuccess(
    { patchState, getState }: StateContext<DocumentsModel>,
    { orgId, customDocType }: Documents.SetCustomDocumentTypesSuccess
  ): void {
    const state = getState();

    patchState({
      isCustomDocumentTypesLoading: false,
      customDocumentTypesMap: {
        ...state.customDocumentTypesMap,
        [orgId]: customDocType
      }
    });

    this.snackbar.open("Custom document types updated", undefined, { duration: 3000 });
  }

  @Action(Documents.SetCustomDocumentTypesFail)
  setCustomDocTypesFail(
    { patchState }: StateContext<DocumentsModel>,
    { error }: Documents.SetCustomDocumentTypesFail
  ): void {
    patchState({ isCustomDocumentTypesLoading: false });

    this.snackbar.open("Failed to update custom document types", "Dismiss", { duration: 5000 });
    console.error(error);
  }
}
